/**
 * localStorage, which in a private window or with site data blocked is not
 * merely empty - reading and writing it throw outright. Nothing stored here is
 * worth breaking the page over, so every access goes through these and a
 * blocked browser simply gets the fallback.
 */

/**
 * The one key every explanation the game gives unasked is recorded under,
 * holding the list of their ids.
 *
 * One key rather than one each: teaching something new then costs an id rather
 * than another clause on the privacy page, and forgetting the lot is a single
 * delete.
 */
export const TUTORIALS_KEY = 'unkoad.tutorialsSeen';

/** Reads a stored preference, ignoring anything that is not still on offer. */
export function restore(storage, key, allowed, fallback) {
    if (!storage) return fallback;
    try {
        const stored = storage.getItem(key);
        return allowed.includes(stored) ? stored : fallback;
    } catch {
        return fallback;
    }
}

export function persist(storage, key, value) {
    if (!storage) return;
    try {
        storage.setItem(key, value);
    } catch {
        // The choice still applies to this visit, it just will not be waiting
        // on the next one.
    }
}

/**
 * A stored value parsed as JSON, or null for anything unreadable - absent,
 * blocked, or left behind by a version that wrote something else here.
 */
export function readJSON(storage, key) {
    if (!storage) return null;
    try {
        return JSON.parse(storage.getItem(key));
    } catch {
        return null;
    }
}

/** Whether this explanation has already been given. */
export function hasSeen(storage, id) {
    return seenIds(storage).includes(id);
}

/**
 * Records an explanation as given.
 *
 * Read-modify-write, so two tabs marking different ids at the same moment can
 * lose one of them. The cost of losing one is that its explanation appears once
 * more, which is not worth locking anything over.
 */
export function markSeen(storage, id) {
    const seen = seenIds(storage);
    if (seen.includes(id)) return;
    persist(storage, TUTORIALS_KEY, JSON.stringify([...seen, id]));
}

/**
 * The ids recorded so far.
 *
 * Anything unreadable counts as nothing seen, which explains one thing twice
 * rather than never explaining it at all.
 */
function seenIds(storage) {
    const parsed = readJSON(storage, TUTORIALS_KEY);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id) => typeof id === 'string');
}
